// Progress storage utilities
// Stores individual verse comparison results and aggregates for progress page

import { saveStudentProgressSnapshot } from './parentStudentFirestore';

export interface VerseScoreEntry {
  verseKey: string; // parasha:verseIndex
  parasha: string;
  verseIndex: number;
  score: number;
  pronunciation: number;
  fluency: number;
  tone: number;
  durationSec: number;
  dateISO: string; // UTC date-time
}

const STORAGE_KEY = 'verseScores';
const DURATION_KEY = 'practiceSeconds';

function safeParse<T>(raw: string | null, fallback: T): T {
  if (!raw) return fallback;
  try {
    return JSON.parse(raw) as T;
  } catch {
    return fallback;
  }
}

export function loadScores(): VerseScoreEntry[] {
  if (typeof window === 'undefined') return [];
  return safeParse<VerseScoreEntry[]>(localStorage.getItem(STORAGE_KEY), []);
}

export function saveVerseScore(entry: Omit<VerseScoreEntry, 'dateISO'> & { dateISO?: string }) {
  if (typeof window === 'undefined') return;
  const list = loadScores();
  const dateISO = entry.dateISO || new Date().toISOString();
  const newEntry: VerseScoreEntry = { ...entry, dateISO } as VerseScoreEntry;
  // Keep ALL attempts (removed same-day overwrite so we can distinguish best vs last)
  const appended = [...list, newEntry];
  const trimmed = appended.sort((a, b) => a.dateISO.localeCompare(b.dateISO)).slice(-500);
  localStorage.setItem(STORAGE_KEY, JSON.stringify(trimmed));
  // Persistent best score per verseKey
  const bestMap = safeParse<Record<string, number>>(localStorage.getItem('verseBestScores'), {});
  if (!bestMap[newEntry.verseKey] || newEntry.score > bestMap[newEntry.verseKey]) {
    bestMap[newEntry.verseKey] = newEntry.score;
    localStorage.setItem('verseBestScores', JSON.stringify(bestMap));
  }
  // Accumulate duration
  const prevSec = safeParse<number>(localStorage.getItem(DURATION_KEY), 0);
  localStorage.setItem(DURATION_KEY, String(prevSec + (entry.durationSec || 0)));
  // Async sync to Firestore so parents can view progress
  (async () => {
    try {
      const userRaw = localStorage.getItem('loggedInUser');
      if (!userRaw) return;
      const user = safeParse<{
        email?: string;
        displayName?: string;
        name?: string;
        userId?: string;
      }>(userRaw, {});
      if (!user.email) return;
      const agg = computeAggregates();
      await saveStudentProgressSnapshot({
        email: user.email,
        name: user.displayName || user.name || '',
        userId: user.userId,
        totalVerses: agg.totalVerses,
        averageScore: agg.averageScore,
        hoursLearned: agg.hoursLearned,
        dayStreak: agg.dayStreak,
        dailySeries: agg.dailySeries,
        parasha: safeParse<{ english: string; hebrew: string } | null>(
          localStorage.getItem('selectedParasha'),
          null,
        ),
      });
    } catch {
      /* non-blocking */
    }
  })();
}

/** Call this whenever user-visible state changes (e.g. parasha selection) so parents see fresh data immediately. */
export async function syncSnapshotToFirestore(
  parashaOverride?: { english: string; hebrew: string } | null,
): Promise<void> {
  try {
    const userRaw = localStorage.getItem('loggedInUser');
    if (!userRaw) return;
    const user = safeParse<{
      email?: string;
      displayName?: string;
      name?: string;
      userId?: string;
    }>(userRaw, {});
    if (!user.email) return;
    const agg = computeAggregates();
    const parasha =
      parashaOverride !== undefined
        ? parashaOverride
        : safeParse<{ english: string; hebrew: string } | null>(
            localStorage.getItem('selectedParasha'),
            null,
          );
    await saveStudentProgressSnapshot({
      email: user.email,
      name: user.displayName || user.name || '',
      userId: user.userId,
      totalVerses: agg.totalVerses,
      averageScore: agg.averageScore,
      hoursLearned: agg.hoursLearned,
      dayStreak: agg.dayStreak,
      dailySeries: agg.dailySeries,
      parasha,
    });
  } catch {
    /* non-blocking */
  }
}

export function loadBestScores(): Record<string, number> {
  if (typeof window === 'undefined') return {};
  return safeParse<Record<string, number>>(localStorage.getItem('verseBestScores'), {});
}

export interface Aggregates {
  totalVerses: number; // unique verses practiced
  averageScore: number; // across all entries
  hoursLearned: number; // accumulated seconds / 3600
  dayStreak: number; // consecutive days with >=1 entry until today
  dailySeries: { date: string; avg: number; count: number }[]; // last 7 days (Mon..Sun) maybe
  highScores: VerseScoreEntry[]; // entries with score >= 90 sorted desc by score then date
  achievements: {
    firstVerse: boolean;
    perfectScore: boolean; // any >= 98
    fiveDayStreak: boolean;
    tenDayStreak: boolean;
    master: boolean; // average >=95 and totalVerses >= 50
    torahScholar: boolean; // totalVerses >= 100
  };
}

export function computeAggregates(): Aggregates {
  const scores = loadScores();
  const uniqueVerseKeys = new Set(scores.map((s) => s.verseKey));
  const totalVerses = uniqueVerseKeys.size;
  const averageScore = scores.length
    ? Math.round(scores.reduce((s, e) => s + e.score, 0) / scores.length)
    : 0;
  const practiceSeconds = safeParse<number>(localStorage.getItem(DURATION_KEY), 0);
  // Streak
  const datesWithPractice = new Set(scores.map((s) => s.dateISO.slice(0, 10)));
  let streak = 0;
  const today = new Date();
  for (let i = 0; i < 30; i++) {
    const d = new Date(
      Date.UTC(today.getUTCFullYear(), today.getUTCMonth(), today.getUTCDate() - i),
    );
    const key = d.toISOString().slice(0, 10);
    if (datesWithPractice.has(key)) streak++;
    else break;
  }
  // Last 7 days daily averages
  const dailyMap: Record<string, { sum: number; count: number }> = {};
  for (const s of scores) {
    const day = s.dateISO.slice(0, 10);
    if (!dailyMap[day]) dailyMap[day] = { sum: 0, count: 0 };
    dailyMap[day].sum += s.score;
    dailyMap[day].count++;
  }
  const dailySeries: { date: string; avg: number; count: number }[] = [];
  for (let i = 6; i >= 0; i--) {
    const d = new Date(
      Date.UTC(today.getUTCFullYear(), today.getUTCMonth(), today.getUTCDate() - i),
    );
    const key = d.toISOString().slice(0, 10);
    const rec = dailyMap[key];
    dailySeries.push({
      date: key,
      avg: rec ? Math.round(rec.sum / rec.count) : 0,
      count: rec ? rec.count : 0,
    });
  }
  const highScores = scores
    .filter((s) => s.score >= 90)
    .sort((a, b) => b.score - a.score || b.dateISO.localeCompare(a.dateISO))
    .slice(0, 15);
  const achievements = {
    firstVerse: scores.length > 0,
    perfectScore: scores.some((s) => s.score >= 98),
    fiveDayStreak: streak >= 5,
    tenDayStreak: streak >= 10,
    master: averageScore >= 95 && totalVerses >= 50,
    torahScholar: totalVerses >= 100,
  };
  return {
    totalVerses,
    averageScore,
    hoursLearned: +(practiceSeconds / 3600).toFixed(1),
    dayStreak: streak,
    dailySeries,
    highScores,
    achievements,
  };
}
